import type { Router } from 'vue-router'
import { getEntityNameFromRoute } from '~/cynqueModules/getEntityNameFromRoute'

/**
 * Client-side runtime singleton for the router.
 *
 * WHY A MODULE SINGLETON: this app is SPA-only (`ssr: false`), so there is
 * exactly one router for the life of the browser session and no per-request
 * isolation to worry about. It also sidesteps Nuxt 4's async-context
 * limitation: calling `useNuxtApp()` AFTER an `await` can throw "nuxt instance
 * unavailable", and much of the code that needs the router (service error
 * handlers, logout) runs in exactly those post-await positions. Capturing it
 * once at boot and reading it back synchronously avoids that.
 *
 * Nuxt 4 removed the Nuxt-2 global `window.$nuxt` (and its `.context`,
 * `.$store`). This module is what replaced it for non-component code. The store
 * half is gone as of P4b — every caller now imports the Pinia store it needs
 * (`useXStore()`), or resolves one by name through piniaStoreRegistry when the
 * namespace is only known at runtime.
 */

let _router: Router | undefined

/**
 * Register the router. Called once from plugins/00.store.ts (the first user
 * plugin), so it is available before any middleware, page, or service code runs.
 */
export function setNuxtRuntime(router: Router): void {
  _router = router
}

/**
 * The router. Nuxt 2 attached `$router` to the root Vuex store instance, which
 * `store/user.js`'s LOGOUT mutation used. Nothing is attached to a Pinia store —
 * `this` in an action is the store itself — so `stores/user.ts` reaches the
 * router here instead.
 */
export function useNuxtRouter(): Router {
  if (!_router) {
    throw new Error(
      '[nuxtRuntime] Router read before initialisation — plugins/00.store.ts must run first'
    )
  }
  return _router
}

/**
 * A Nuxt-2-shaped `context` object rebuilt from Nuxt 4 primitives, so the
 * error-handling code that still expects `{ route, redirect, error }`
 * (cynqueModules/handleAsyncDataErrors.ts and its non-component callers) keeps
 * working unchanged. Replaces the old `window.$nuxt.context`.
 *
 * `error` uses Nuxt 4's auto-imported `showError` (the successor to Nuxt 2's
 * `context.error`); `redirect` uses the captured router's `push` (the successor
 * to `context.redirect`). Migrating handleAsyncDataErrors off the context shape
 * entirely is deferred to the component/error-handling pass.
 *
 * Also carries `params` and `$getEntityNameFromRoute` so that page `asyncData`
 * bodies — which used the Nuxt-2 `context.params.id` /
 * `context.$getEntityNameFromRoute()` — keep working verbatim once they switch
 * from `defineComponent` to `defineNuxtComponent` and rebuild `context` from
 * this shim (see the Nuxt-4 asyncData compat pattern). The SPA singleton reads
 * `router.currentRoute.value`, which — by the time a page's Suspense async
 * setup (asyncData) runs — is already the confirmed target route.
 */
export function getNuxtContext() {
  if (!_router) {
    throw new Error(
      '[nuxtRuntime] Router read before initialisation — plugins/00.store.ts must run first'
    )
  }
  const route = _router.currentRoute.value
  return {
    route,
    params: route.params,
    redirect: (to: any) => _router!.push(to),
    // `showError` is a Nuxt 4 auto-import (client-safe); referenced lazily here
    // so merely importing this module (e.g. from a unit test) never touches it.
    error: (e: any) => showError(e),
    $getEntityNameFromRoute: () =>
      getEntityNameFromRoute(route.name?.toString()),
    app: { router: _router },
  }
}
